import './MembershipCard.css';

import React, { useState } from 'react';
import { generateClassName, toLocaleDateTime } from '../../../b2b-common/helpers';
import { Box, Tooltip } from '@mui/material';
import FieldText from '../../../b2b-common/FieldText';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';
import MoreButton from '../../../b2b-common/MoreButton';
import { Delete, Key, KeyOff, MoreVert } from '@mui/icons-material';
import useMembershipManagement from '../../hooks/useMembershipManagement';
import ConfirmationDialog from '../../../b2b-common/ConfirmationDialog';

const EnabledBox = ({ isEnabled = true, top = null, left = null, right = null, bottom = null }) => {
    const { t } = useB2BTranslation('b2b-users');

    const tooltipText = isEnabled ? t('Enabled') : t('Disabled');

    return (
        <Tooltip title={tooltipText} arrow>
            <Box
                className={'enabled-box'}
                position={'absolute'}
                top={top}
                left={left}
                right={right}
                bottom={bottom}
                width={20}
                height={20}
                borderRadius={10}
                bgcolor={isEnabled ? 'success.main' : 'error.main'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                color={'white'}
                border={2}
                fontSize={12}
            />
        </Tooltip>
    );
}

const MembershipCard = ({ membership, className = null, onMembershipUpdated = null }) => {
    const { t } = useB2BTranslation('b2b-users');
    const { enableDisableMembership, removeMembership } = useMembershipManagement({ membership });
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const user = membership.user;

    const handleEnableDisableMembership = async () => {
        await enableDisableMembership();
        if (onMembershipUpdated) {
            onMembershipUpdated(membership);
        }
    }

    const handleDeleteMembership = async () => {
        await removeMembership();
        setShowDeleteConfirmation(false);
        if (onMembershipUpdated) {
            onMembershipUpdated(membership);
        }
    }

    return (
        <Box className={generateClassName('membership-card', '', className)} display={'flex'} flexDirection={'row'} gap={2} padding={2} borderRadius={2} boxShadow={1} bgcolor={'background.paper'} position={'relative'}>
            <Box className={'membership-avatar'} display={'flex'} alignItems={'center'} justifyContent={'center'} width={50} height={50} borderRadius={25} bgcolor={'primary.main'} color={'white'} fontSize={24}>
                {user.firstName[0]}{user.lastName[0]}
            </Box>
            <Box className={'membership-info'} display={'flex'} flexDirection={'column'} justifyContent={'flex-start'} alignContent={'flex-start'} alignItems={'flex-start'} width={`calc(100% - 50)`}>
                <Box className={'membership-name'} fontWeight={600}>
                    {user.firstName} {user.lastName}
                </Box>
                <Box className={'membership-email'} color={'text.secondary'}>
                    {user.email}
                </Box>
                <Box className={'membership-roles'} color={'text.secondary'} fontSize={12} padding={0}>
                    {membership.roles.map((role, index) => (
                        <span key={index}>
                            {role.roleName}
                            {index < membership.roles.length - 1 && ', '}
                        </span>
                    ))}
                </Box>
                <Box pt={2} display={'flex'} flexDirection={'row'} gap={3} alignItems={'center'} justifyContent={'flex-start'} width={'100%'}>
                    <FieldText
                        label={t('Membership date')} labelAlign='left'
                        text={toLocaleDateTime(membership.membershipDate)} textAlign='left'
                        className={'membership-date'}
                    />
                    <FieldText
                        label={t('Last login')} labelAlign='left'
                        text={toLocaleDateTime(membership.lastLoginDate, true)} textAlign='left'
                        className={'membership-date'}
                    />
                </Box>
            </Box>
            <EnabledBox
                isEnabled={membership.enabled}
                top={10}
                left={10}
            />
            <MoreButton icon={<MoreVert />}
                buttons={[
                    ...(membership.enabled
                        ? [
                            {
                                label: t('Disable'),
                                icon: <KeyOff />,
                                rolesAllowed: ['ADMIN', 'SUPER_ADMIN'],
                                onClick: handleEnableDisableMembership,
                            },
                        ]
                        : [
                            {
                                label: t('Enable'),
                                icon: <Key />,
                                rolesAllowed: ['ADMIN', 'SUPER_ADMIN'],
                                onClick: handleEnableDisableMembership,
                            },
                        ]),
                    {
                        label: t('Delete'),
                        icon: <Delete />,
                        rolesAllowed: ['ADMIN'],
                        onClick: () => setShowDeleteConfirmation(true),
                    },
                ]}
                dropPositionAlign='right'
                rolesAllowed={['ADMIN', 'SUPER_ADMIN']}
                className={'jb01-more-button'}
            />
            <ConfirmationDialog
                title={t('Removal Confirmation Request')}
                mainText={t('Please confirm you want to completely remove the membership')}
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteMembership}
            />
        </Box>
    );
}

export default MembershipCard;